import useDeviceDetect from 'Hooks/useDeviceDetect';
import { store } from 'Models/Store';
import React from 'react';
import alertToast from 'Util/ToastifyUtils';
import { GenericStaffCollection } from 'Views/Components/_HumanWritten/StaffTables/GenericStaffCollection';
import { SortableHeaderButton } from 'Views/Components/_HumanWritten/StaffTables/SortableHeaderButton';
import {
	StaffTransactionsFilters,
	useStaffTransactionsStore,
} from 'Views/Components/_HumanWritten/StaffTables/StaffTransactions/StaffTransactionsFilterStore';
import { TransactionColumnKeys } from 'Views/Components/_HumanWritten/StaffTables/StaffTransactions/TransactionColumnKeys';
import { TransactionRender } from 'Views/Components/_HumanWritten/StaffTables/StaffTransactions/TransactionRender';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import If from 'Views/Components/If/If';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';

/**
 * A booking's id paired with the reference shown for it. The cell displays the reference but must link
 * by the id: linking by reference resolves the target with a first-match lookup, which opens an
 * arbitrary booking when two share a reference.
 */
export interface BookingReferenceDto {
	id: string;
	humanReadableId: string;
}

export interface TransactionListSummaryDto {
	id: string;
	bookingIds: string[];
	/** Same bookings as `bookingIds`, with their ids, for linking. */
	bookingLinks?: BookingReferenceDto[] | null;
	humanReadableId: string;
	name: string;
	email: string;
	userId: string;
	userDisplayName?: string;
	totalAmount?: number;
	paidAmount?: number;
	status: string;
	// cardType?: string;
	createdDate: Date;
	paidAt?: Date;
	// bookingCount: number;
	expirationTime?: Date;
	invoiceNumber?: string;
	inCheckIn: boolean;
	purchasedVoucherCode?: string;
	purchasedEntitlementCode?: string;
	purchasedItemId?: string;
	/** The charter this transaction is for, by name. Absent on everything else. */
	charterName?: string;
	/** That charter's id, which the References cell links to. Absent on everything else. */
	charterId?: string;
	/**
	 * That charter's reference, which is what the References cell shows. Absent on a charter taken
	 * before charters had one, which falls back to the name.
	 */
	charterHumanReadableId?: string;
	refundVoucherCodes?: string[];
	/** Vouchers redeemed against the transaction, as opposed to issued by it. */
	usedVoucherCodes?: string[];
	typeOfTransaction: string;
	entityType: string;
	isEventBooking: boolean;
	isTerminalPayment: boolean;
	hasVoucher: boolean;
	hasCancellationFee: boolean;
	// Whether the "Change fees" action applies — derived server-side so the button and the endpoint
	// can't disagree. See FilteredTransactionsProjection.
	canEditFees: boolean;
	createdByType: string;
	createdByName?: string;
	parkingReservationIds?: string[];
	refundIds?: string[];
}

export interface StaffTransactionsCollectionProps {
	transactions: TransactionListSummaryDto[] | null;
	totalTransactionCount: number | undefined;
	perPage: number;
	filters: StaffTransactionsFilters;
	setFilters: (newFilters: StaffTransactionsFilters) => void;
	className?: string;
	isLoading: boolean;
	selectedTransactionIds: Set<string>;
	setSelectedTransactionIds: (ids: Set<string>) => void;
	lastUpdated: Date;
	refresh: () => void;
	hideCustomerColumn?: boolean;
}

export enum TransactionOrderByOption {
	TRANSACTION_ID = 'TRANSACTION_ID',
	CUSTOMER_EMAIL = 'CUSTOMER_EMAIL',
	CUSTOMER_NAME = 'CUSTOMER_NAME',
	AMOUNT = 'AMOUNT',
	CREATED_DATE = 'CREATED_DATE',
	STATUS = 'STATUS',
}

export function StaffTransactionsCollection({
	transactions,
	totalTransactionCount,
	perPage,
	filters,
	setFilters,
	className,
	isLoading,
	setSelectedTransactionIds,
	selectedTransactionIds,
	lastUpdated,
	refresh,
	hideCustomerColumn,
}: StaffTransactionsCollectionProps) {
	const { isMobile } = useDeviceDetect();
	const { visibleColumns } = useStaffTransactionsStore();

	const isColumnVisible = (key: string) => {
		if (hideCustomerColumn && key === TransactionColumnKeys.CUSTOMER) {
			return false;
		}
		const column = visibleColumns.find(col => col.key === key);
		if (!column) return false;
		return column.visible;
	};

	const buttons = () => {
		return (
			<Button
				newStyles
				display={Display.Outline}
				colors={Colors.Secondary}
				onClick={() => {
					setSelectedTransactionIds(new Set());
				}}
			>
				Clear selection
			</Button>
		);
	};

	return (
		<>
			<If condition={selectedTransactionIds.size > 0 && store.isManager}>
				<div className="selected-transaction-actions__container">
					<MessageBox
						title={`${selectedTransactionIds.size} transaction(s) selected`}
						status="info"
						actions={buttons()}
					/>
				</div>
			</If>
			<GenericStaffCollection
				items={transactions}
				totalItemCount={totalTransactionCount}
				perPage={perPage}
				currentPage={filters.pageNo}
				onPageChange={page => setFilters({ ...filters, pageNo: page })}
				isLoading={isLoading}
				selectedItemIds={selectedTransactionIds}
				setSelectedItemIds={setSelectedTransactionIds}
				lastUpdated={lastUpdated}
				className="staff__table"
				renderRow={(transaction, isChecked, toggle) => (
					<TransactionRender
						transaction={transaction}
						isChecked={isChecked}
						onToggleCheckbox={toggle}
						isColumnVisible={isColumnVisible}
					/>
				)}
				columns={[
					// Selection only drives the manager-only bulk action bar above, so don't render a
					// checkbox column staff can tick to no effect.
					...(store.isManager ? [{ key: 'checkbox', label: '' }] : []),
					{ key: 'check-in', label: '', className: 'minimum' },
					{
						key: 'id',
						label: 'Transaction ID',
						headerComponent: (
							<SortableHeaderButton
								text="Transaction ID"
								orderByField={TransactionOrderByOption.TRANSACTION_ID}
								currentOrderBy={filters.orderBy}
								isDescending={filters.orderByDescending}
								onSort={(field, desc) =>
									setFilters({
										...filters,
										orderBy: field as TransactionOrderByOption,
										orderByDescending: desc,
										pageNo: 0,
									})
								}
							/>
						),
					},
					...(isColumnVisible(TransactionColumnKeys.CREATED_DATE)
						? [
								{
									key: 'created',
									label: 'Created',
									headerComponent: (
										<SortableHeaderButton
											text="Created"
											orderByField={TransactionOrderByOption.CREATED_DATE}
											currentOrderBy={filters.orderBy}
											isDescending={filters.orderByDescending}
											onSort={(field: string, desc: boolean) =>
												setFilters({
													...filters,
													orderBy: field as TransactionOrderByOption,
													orderByDescending: desc,
													pageNo: 0,
												})
											}
										/>
									),
								},
							]
						: []),
					...(isColumnVisible(TransactionColumnKeys.CUSTOMER)
						? [
								{
									key: 'customer',
									label: 'Customer',
								},
							]
						: []),
					...(isColumnVisible(TransactionColumnKeys.BOOKINGS)
						? [
								{
									key: 'bookings',
									label: 'References',
								},
							]
						: []),
					...(isColumnVisible(TransactionColumnKeys.ENTITY_TYPE)
						? [
								{
									key: 'entityType',
									label: 'Type',
								},
							]
						: []),
					...(isColumnVisible(TransactionColumnKeys.TYPE)
						? [
								{
									key: 'type',
									label: 'Action',
								},
							]
						: []),
					...(isColumnVisible(TransactionColumnKeys.AMOUNT)
						? [
								{
									key: 'amount',
									label: 'Amount ($)',
								},
							]
						: []),
					...(isColumnVisible(TransactionColumnKeys.STATUS)
						? [
								{
									key: 'status',
									label: 'Status',
								},
							]
						: []),
					...(isColumnVisible(TransactionColumnKeys.CREATED_BY)
						? [
								{
									key: 'createdBy',
									label: 'Created By',
								},
							]
						: []),
					{ key: 'actions', label: '' },
				]}
				emptyMessage="No transactions found"
				loadingMessage="Loading transactions"
				testId="transactions-table"
			/>
		</>
	);
}
